import {
  Button,
  ButtonVariant,
  Icon,
  IconName,
  IconScale,
  IFalconTheme,
  Layout,
  LineItem,
  LineItemSizeVariant,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  TooltipPosition,
} from '@se-admin/falcon';
import { i18n } from '@secret-escapes/i18n';
import * as React from 'react';
import { ITranslationObject } from '../../common/types';
import { ICalendarSelectionSummary } from '../../common/types/apiSchema';
import {
  CalendarInfoContainer,
  CalendarInfoContent,
  LineItemContainer,
  NoteContainer,
  PriceBreakdownSection,
  StyledButton,
  StyledTooltip,
  StyledTooltipTitle,
} from './BookingAmendments.styles';

export { CalendarInfo };

interface ICalendarInfoProps {
  disabled: boolean;
  defaultDateSelected: boolean;
  handleShowDeals: () => void;
  handleCancel: () => void;
  isLoadingDraftDeals: boolean;
  translations: ITranslationObject;
  theme: IFalconTheme;
  calendarSelectionSummary: ICalendarSelectionSummary;
  rooms: number;
  endDate?: string;
}

const showTooltip: boolean = true;
const popoverZIndex: number = 10000;

const CalendarInfo: React.FunctionComponent<ICalendarInfoProps> = ({
  disabled,
  defaultDateSelected,
  handleShowDeals,
  handleCancel,
  isLoadingDraftDeals,
  translations,
  theme,
  calendarSelectionSummary,
  rooms,
  endDate,
}) => {
  return (
    <CalendarInfoContainer data-testid={'calendarInfo'}>
      <CalendarInfoContent>
        <PriceBreakdownSection>
          {calendarSelectionSummary?.resortFeeDisclaimer && endDate && (
            <LineItemContainer>
              <Layout.Container alignItems={'center'}>
                <ResponsiveTypography variant={ResponsiveTypographyVariant.body}>
                  {i18n('sale.mandatoryFees.feesCollected', [], translations)}
                </ResponsiveTypography>
                {showTooltip && (
                  <StyledTooltip
                    content={
                      <React.Fragment>
                        <StyledTooltipTitle
                          variant={ResponsiveTypographyVariant.t4SansBold}
                          color={theme.color.grey60}
                        >
                          {i18n('sale.mandatoryFees.payedAtHotel', [], translations)}
                        </StyledTooltipTitle>
                        <ResponsiveTypography
                          variant={ResponsiveTypographyVariant.body}
                          color={theme.color.grey60}
                          allowDangerousRender={true}
                        >
                          {calendarSelectionSummary?.resortFeeDisclaimer}
                        </ResponsiveTypography>
                      </React.Fragment>
                    }
                    position={TooltipPosition.top}
                    zIndex={popoverZIndex}
                  >
                    <Icon name={IconName.info} fillColor={'black'} size={IconScale.m} />
                  </StyledTooltip>
                )}
              </Layout.Container>
            </LineItemContainer>
          )}
          <LineItemContainer>
            <LineItem
              title={i18n('bookings.guests.rooms', [], translations)}
              value={i18n('common.numberOfRooms.text', [rooms], translations)}
              bold={false}
              sizeVariant={LineItemSizeVariant.small}
            />
          </LineItemContainer>
          <LineItemContainer>
            <LineItem
              title={`${i18n('cancellation.paid', [], translations)} (${i18n(
                'sale.incl.taxes',
                [],
                translations
              )})`}
              value={calendarSelectionSummary?.alreadyPaidBreakdown?.total?.forDisplay}
              bold={false}
              sizeVariant={LineItemSizeVariant.small}
            />
          </LineItemContainer>
          {endDate && (
            <React.Fragment>
              <LineItemContainer>
                <LineItem
                  title={`${i18n('amendments.new.total', [], translations)} (${i18n(
                    'sale.incl.taxes',
                    [],
                    translations
                  )})`}
                  value={calendarSelectionSummary?.priceBreakdown?.total?.forDisplay}
                  bold={false}
                  sizeVariant={LineItemSizeVariant.small}
                />
              </LineItemContainer>
              <LineItemContainer>
                <LineItem
                  title={i18n(
                    calendarSelectionSummary?.balance?.unit < 0
                      ? 'amendments.refund'
                      : 'amendments.difference.pay',
                    [],
                    translations
                  )}
                  value={calendarSelectionSummary?.balance?.forDisplay}
                  bold={true}
                  sizeVariant={LineItemSizeVariant.medium}
                />
              </LineItemContainer>
            </React.Fragment>
          )}
        </PriceBreakdownSection>
        {calendarSelectionSummary?.cancellationSelectionInformation && (
          <NoteContainer>
            <ResponsiveTypography
              color={theme.color.grey60}
              variant={ResponsiveTypographyVariant.body}
              allowDangerousRender={true}
            >
              {calendarSelectionSummary?.cancellationSelectionInformation}
            </ResponsiveTypography>
          </NoteContainer>
        )}
        <Layout.Container flexDirection={'row'} justifyContent={'space-between'}>
          <StyledButton
            variant={ButtonVariant.secondary}
            onClick={handleCancel}
            dataAttributes={{ testid: 'cancel-amendment' }}
            fullWidth={true}
          >
            {i18n('amendments.cancel.changes', [], translations)}
          </StyledButton>
          <Button
            onClick={handleShowDeals}
            showLoader={isLoadingDraftDeals}
            disabled={disabled || defaultDateSelected}
            dataAttributes={{ testid: 'confirm-amendment' }}
            fullWidth={true}
          >
            {i18n('amendments.review.changes', [], translations)}
          </Button>
        </Layout.Container>
      </CalendarInfoContent>
    </CalendarInfoContainer>
  );
};
